import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AlertMessage } from "../../../../common/data/AlertMsg";
import { TooltipMessage } from "../../../../common/data/TooltipMsg";
import {
  inProgress,
  isEmptyArray,
  isNullOrUndefined,
  isNullOrUndefinedOrEmpty,
  isTableDataAvailable,
} from "../../../../common/utils";
import { defaultPageSize } from "../../../../components/authZTable";
import { InfoTabUsersTableContentActions } from "../../../../components/InfoTab/model";
import {
  sendUserGroupDetailsEvent,
  UserGroupDetailsAction,
} from "../../../../components/matomoClient";
import { UserGroupActions } from "../../model";
import { userGroupDetailsTabTypes } from "../model";
import { membersKey, ownersKey, userGroupInfoCalledBy } from "./model";

export function UserTablePropsGenerator(
  users,
  userRoleKey,
  userGroupUniqueName,
  userGroupName,
  userGroupRepresentationName
) {
  const dispatch = useDispatch();
  const tabName =
    userRoleKey === ownersKey ? userGroupDetailsTabTypes.Info : userGroupDetailsTabTypes.Members;

  const userGroupUsersDetailsProgress = useSelector(
    (rootState) =>
      rootState.InfoTabUsersTableContent.userGroupUserInfoTableContent[userGroupUniqueName]?.[
        userRoleKey
      ]?.progress
  );
  const userGroupUsersDetailsError = useSelector(
    (rootState) =>
      rootState.InfoTabUsersTableContent.userGroupUserInfoTableContent[userGroupUniqueName]?.[
        userRoleKey
      ]?.error
  );
  const userGroupUsersDetails = useSelector(
    (rootState) =>
      rootState.InfoTabUsersTableContent.userGroupUserInfoTableContent[userGroupUniqueName]?.[
        userRoleKey
      ]?.value
  );

  const userGroupDetails = useSelector(
    (rootState) => rootState.userGroup.userGroupDetails[userGroupUniqueName]?.value
  );

  const userGroupUserTableUnSelectedColumn = useSelector(
    (rootState) =>
      rootState.userGroup.userGroupDetailsTabStates[getUserGroupId()]?.[tabName]?.unSelectedColumns
  );

  let userGroupIndividualInclusionMembers = useSelector(
    (rootState) => rootState.userGroup.userGroupDetails[userGroupUniqueName]?.value?.individualInclusionMembers
  );

  let excludedMembers = useSelector(
    (rootState) => rootState.userGroup.userGroupDetails[userGroupUniqueName]?.value?.excludedMembers
  );

  let userTablePageSize = useSelector(
    (rootState) =>
      rootState.userGroup.userGroupDetailsTabStates[getUserGroupId()]?.[tabName]?.pageSize
  );

  const isLoading = inProgress(userGroupUsersDetailsProgress);

  const isErrorState = !isNullOrUndefined(userGroupUsersDetailsError);

  const totalColumns = userGroupUsersDetails?.columns?.map((column) => column.title);

  function findSelectedColumns() {
    let selectedColumns = [];
    if (!isNullOrUndefined(userGroupUserTableUnSelectedColumn)) {
      totalColumns?.forEach((column) => {
        if (!userGroupUserTableUnSelectedColumn.includes(column)) {
          selectedColumns.push(column);
        }
      });
    }
    if (selectedColumns?.length === 0) {
      selectedColumns = totalColumns;
    }

    let userTableColumns;
    if (selectedColumns?.length > 0) {
      userTableColumns = userGroupUsersDetails?.columns?.filter(({ title }) =>
        selectedColumns.includes(title)
      );
    }
    return { userTableColumns, selectedColumns };
  }

  let { userTableColumns, selectedColumns } = findSelectedColumns();

  let copyEmailList = "";
  userGroupUsersDetails?.dataSource?.forEach((user, index) => {
    if ((userRoleKey === membersKey && !excludedMembers?.some((excludedMember) => { return excludedMember.userId === user.key })) || userRoleKey === ownersKey) {
      if (index === 0) {
        copyEmailList = `${copyEmailList}${user.email}`;
      } else {
        copyEmailList = `${copyEmailList},${user.email}`;
      }
    }
  });

  function copyAllEmailsMatomoFn() {
    sendUserGroupDetailsEvent(
      UserGroupDetailsAction.CopyAllEmails.users(userGroupRepresentationName, userRoleKey),
      userGroupName
    );
  }

  function setTabPageSize({ pageSize }) {
    let tabName;
    if (userRoleKey === ownersKey) {
      tabName = userGroupDetailsTabTypes.Info;
    } else if (userRoleKey === membersKey) {
      tabName = userGroupDetailsTabTypes.Members;
    }
    dispatch(UserGroupActions.setTabPageSize({ userGroupId: getUserGroupId(), tabName, pageSize }));
  }

  if (isNullOrUndefined(userTablePageSize)) {
    userTablePageSize = defaultPageSize;
  }

  let paginationProps = false;
  if (userRoleKey === membersKey) {
    paginationProps = {
      pageSize: userTablePageSize,
      setTabPageSize: setTabPageSize,
    };
  }

  userGroupIndividualInclusionMembers = userGroupIndividualInclusionMembers?.map((userGroupIndividualInclusionMember) => {
    return userGroupIndividualInclusionMember.userId;
  });

  const userTableProps = {
    copyEmails: {
      copyEmailsList: copyEmailList,
      tooltipMessage: TooltipMessage.copyAllEmails.userGroups.users(
        userGroupRepresentationName,
        userRoleKey
      ),
      alertMessage: AlertMessage.copyAllEmails.userGroups.users(
        userGroupRepresentationName,
        userRoleKey
      ),
      matomoFunction: copyAllEmailsMatomoFn,
    },
    label: `${userGroupRepresentationName} ${userRoleKey}`,
    tableProps: {
      tableId: userGroupUniqueName,
      tableName: `userGroupInfo-${userRoleKey}`,
      dataSource: userGroupUsersDetails?.dataSource,
      columns: userTableColumns,
      pagination: paginationProps,
    },
    noUserMessage: `There are no ${userGroupRepresentationName} ${userRoleKey}`,
    individualInclusionMembers: userGroupIndividualInclusionMembers,
    overridenNoOfUsers: userRoleKey === membersKey? users?.length - excludedMembers?.length : undefined
  };

  const listOfTables = [userTableProps];

  function onColumnSelect(selectedColumns) {
    let unSelectedColumns = [];
    totalColumns.forEach((column) => {
      if (!selectedColumns.includes(column)) {
        unSelectedColumns.push(column);
      }
    });
    dispatch(
      UserGroupActions.setUnSelectedColumns({
        userGroupId: getUserGroupId(),
        tabName,
        unSelectedColumns: unSelectedColumns,
      })
    );
  }

  function getUserGroupId() {
    return userGroupDetails?.id;
  }

  const columnSelectProps = {
    shouldRender:
      !isNullOrUndefined(totalColumns) &&
      !isEmptyArray(totalColumns) &&
      !isLoading &&
      isTableDataAvailable(userGroupUsersDetails?.dataSource),
    totalColumns: totalColumns,
    selectedColumns: selectedColumns,
    onSelected: onColumnSelect,
  };

  useEffect(() => {
    if (userRoleKey === ownersKey) {
      if (!isNullOrUndefined(users)) {
        dispatch(
          InfoTabUsersTableContentActions.getInfoTabUsersTableContent({
            calledBy: userGroupInfoCalledBy,
            userGroupUniqueName,
            usersList: users.toString(),
            userRoleKey: userRoleKey,
          })
        );
      }
    }
    if (userRoleKey === membersKey) {
      if (!isNullOrUndefinedOrEmpty(userGroupUniqueName)) {
        dispatch(
          InfoTabUsersTableContentActions.getUserGroupMembersTableContent(userGroupUniqueName)
        );
      }
    }
  }, [users, userGroupUniqueName]);

  return { isLoading, isErrorState, listOfTables, columnSelectProps };
}
